import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { apiHandler } from '../../../api/apihandler';
import {
  PROFILE_DETAILS,
  PROFILE_COVER_PIC_UPDATE,
  FEEDS_LIST,
  FAVORITE_RECIPE_LIST,
  PEOPLE_YOU_MAY_KNOW,
  USER_FOLLOW_REQUEST,
} from '../../../api/constants';
import { handleImageUpload } from '../../../api/uploads';
import { setUserData } from '../../../store/setting/actions';
import { getAvatarColor, getInitials, apiSucceeded, apiErrorMessage, formatAddress } from '../../../views/dashboard/feed/feedUtils';
import MediaPlaceholderIcon from '../../../components/nemonto/MediaPlaceholderIcon';
import './my-profile-page.css';

const TABS = [
  { key: 'recipes', label: 'Recipes' },
  { key: 'madeit', label: 'Made it' },
  { key: 'challenges', label: 'Challenges' },
  { key: 'saved', label: 'Saved' },
];

const Avatar = ({ url, name, className }) => {
  if (url) return <img src={url} alt={name} className={className} />;
  return (
    <div className={className} style={{ background: getAvatarColor(name || 'User'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
      {getInitials(...(name || 'User').split(' '))}
    </div>
  );
};

// posted_challenges/participated_challenges/made_it_list all come back
// inline on PROFILE_DETAILS itself - no separate fetch needed, unlike
// recipes/saved which have their own list endpoints. `selfOwner` is who to
// attribute the card to when the underlying post has no author of its own
// (e.g. a challenge you hosted) - the current profile.
function mapPostedChallenge(c, selfOwner) {
  return {
    id: c.id,
    kind: 'challenge',
    title: c.title || c.content || 'Challenge',
    excerpt: c.content && c.content !== c.title ? c.content : null,
    image: c.user_post_images?.[0]?.image_path || null,
    badge: 'Hosted',
    ownerName: selfOwner.name,
    ownerPhoto: selfOwner.photo,
  };
}

function mapParticipatedChallenge(p, selfOwner) {
  const post = p?.user_post || {};
  const host = post?.user;
  const hostName = host ? `${host.first_name || ''} ${host.last_name || ''}`.trim() || 'User' : selfOwner.name;
  return {
    id: post.id,
    kind: 'challenge',
    title: post.title || post.content || 'Challenge',
    excerpt: post.content && post.content !== post.title ? post.content : null,
    image: post.user_post_images?.[0]?.image_path || null,
    badge: p?.is_winner === 1 ? 'Winner' : 'Joined',
    ownerName: hostName,
    ownerPhoto: host ? (host.user_setting?.photo || null) : selfOwner.photo,
  };
}

function mapMadeIt(m, selfOwner) {
  const recipeOwner = m.recipe?.user;
  const ownerName = recipeOwner ? `${recipeOwner.first_name || ''} ${recipeOwner.last_name || ''}`.trim() || 'User' : selfOwner.name;
  return {
    id: m.id,
    kind: 'recipe',
    linkId: m.original_recipe_id,
    title: m.recipe?.title || 'Made it',
    excerpt: m.content || null,
    image: m.media?.[0]?.file_url || m.recipe?.user_post_images?.[0]?.image_path || null,
    ownerName,
    ownerPhoto: recipeOwner ? (recipeOwner.user_setting?.photo || null) : selfOwner.photo,
  };
}

// Card design matches FavouriteRecipesPage's .nmsl-recipe-card (thumb on
// top, owner row + title + excerpt below) instead of the old square
// Instagram-style tile, but keeps a 3-column grid instead of that page's 2.
const RecipeCard = ({ item, onOpen }) => (
  <div className="nmpr-recipe-card" onClick={() => onOpen(item)} role="button" tabIndex={0}>
    <div className="nmpr-recipe-thumb">
      {item.image ? <img src={item.image} alt="" /> : <MediaPlaceholderIcon size={48} />}
      {item.badge && <span className="nmpr-recipe-badge">{item.badge}</span>}
    </div>
    <div className="nmpr-recipe-body">
      <div className="nmpr-recipe-owner-row">
        <Avatar url={item.ownerPhoto} name={item.ownerName} className="nmpr-recipe-owner-avatar" />
        <span className="nmpr-recipe-owner-name">{item.ownerName}</span>
      </div>
      <p className="nmpr-recipe-title">{item.title || 'Untitled recipe'}</p>
      {item.excerpt && <p className="nmpr-recipe-excerpt">{item.excerpt}</p>}
    </div>
  </div>
);

const TAB_KEYS = TABS.map((t) => t.key);

const MyProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [profile, setProfile] = useState(null);
  // posted_challenges/participated_challenges/made_it_list (and their
  // _count fields) come back as siblings of `user` on PROFILE_DETAILS,
  // not nested inside it - kept separate from `profile` for that reason.
  const [postedChallenges, setPostedChallenges] = useState([]);
  const [participatedChallenges, setParticipatedChallenges] = useState([]);
  const [madeItList, setMadeItList] = useState([]);
  const [challengeCounts, setChallengeCounts] = useState({ posted: 0, participated: 0 });
  const [madeItCount, setMadeItCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploadingCover, setUploadingCover] = useState(false);

  const initialTab = TAB_KEYS.includes(location.state?.tab) ? location.state.tab : 'recipes';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [recipes, setRecipes] = useState([]);
  const [recipesLoaded, setRecipesLoaded] = useState(false);

  const [saved, setSaved] = useState([]);
  const [savedLoaded, setSavedLoaded] = useState(false);
  const [savedLoading, setSavedLoading] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [busyFollowIds, setBusyFollowIds] = useState(new Set());

  async function fetchProfile() {
    setLoading(true);
    try {
      const res = await apiHandler(PROFILE_DETAILS, 'POST');
      if (apiSucceeded(res)) {
        const data = res?.data?.data;
        const user = data?.user;
        setProfile(user);
        setPostedChallenges(data?.posted_challenges || []);
        setParticipatedChallenges(data?.participated_challenges || []);
        setMadeItList(data?.made_it_list || []);
        setChallengeCounts({
          posted: data?.posted_challenges_count ?? (data?.posted_challenges || []).length,
          participated: data?.participated_challenges_count ?? (data?.participated_challenges || []).length,
        });
        setMadeItCount(data?.made_it_count ?? (data?.made_it_list || []).length);
        dispatch(setUserData(JSON.stringify(user)));
      } else {
        Swal.fire('Unable to load profile', apiErrorMessage(res, 'Please try again.'), 'warning');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchRecipes(userId) {
    try {
      const res = await apiHandler(FEEDS_LIST, 'POST', { user_id: userId, page: 1, limit: 50 });
      if (apiSucceeded(res)) {
        const raw = res?.data?.data?.feeds?.data || [];
        const ownerName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'You';
        const ownerPhoto = profile?.user_setting?.photo || null;
        setRecipes(raw.map((d) => ({
          id: d.id,
          kind: 'recipe',
          title: d.title || '',
          excerpt: d.content || null,
          image: d.user_post_images?.[0]?.image_path || null,
          ownerName,
          ownerPhoto,
        })));
      }
    } catch (e) { console.error(e); }
    finally { setRecipesLoaded(true); }
  }

  async function fetchSaved() {
    setSavedLoading(true);
    try {
      const res = await apiHandler(FAVORITE_RECIPE_LIST, 'POST', { page: 1, limit: 50 });
      if (apiSucceeded(res)) {
        const raw = res?.data?.data?.userFavoriteRecipeList?.recipes || [];
        setSaved(raw.map((r) => ({
          id: r.id,
          kind: 'recipe',
          title: r.title || '',
          excerpt: r.content || null,
          image: r?.medias?.[0]?.image_path || null,
          ownerName: `${r?.owner?.first_name || ''} ${r?.owner?.last_name || ''}`.trim() || 'User',
          ownerPhoto: r?.owner?.user_setting?.photo || r?.owner?.photo || null,
        })));
      }
    } catch (e) { console.error(e); }
    finally { setSavedLoading(false); setSavedLoaded(true); }
  }

  async function fetchSuggestions() {
    try {
      const res = await apiHandler(PEOPLE_YOU_MAY_KNOW, 'POST', { page: 1, limit: 4 });
      if (apiSucceeded(res)) setSuggestions(res?.data?.data?.users?.data || []);
    } catch (_) { /* suggestions stay empty */ }
  }

  useEffect(() => {
    fetchProfile();
    fetchSuggestions();
    if (initialTab === 'saved') fetchSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profile?.id && !recipesLoaded) fetchRecipes(profile.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  function selectTab(key) {
    setActiveTab(key);
    if (key === 'saved' && !savedLoaded) fetchSaved();
  }

  async function toggleFollowSuggestion(userId) {
    if (busyFollowIds.has(userId)) return;
    setBusyFollowIds((prev) => new Set([...prev, userId]));
    try {
      const res = await apiHandler(USER_FOLLOW_REQUEST, 'POST', { follower_id: userId });
      if (apiSucceeded(res)) {
        setSuggestions((prev) => prev.map((u) => (u.id === userId ? { ...u, is_following: u.is_following === 1 ? 0 : 1 } : u)));
      }
    } catch (e) { console.error(e); }
    finally {
      setBusyFollowIds((prev) => { const next = new Set(prev); next.delete(userId); return next; });
    }
  }

  function handleCoverChange(event) {
    const file = event.target.files[0];
    if (!file) return;
    setUploadingCover(true);
    (async () => {
      try {
        const url = await handleImageUpload(file, 'coverImage/', 'image/jpeg');
        const res = await apiHandler(PROFILE_COVER_PIC_UPDATE, 'POST', { cover_photo: url });
        if (apiSucceeded(res)) {
          const user = res?.data?.data?.user;
          setProfile(user);
          dispatch(setUserData(JSON.stringify(user)));
        } else {
          Swal.fire('Error', apiErrorMessage(res, 'Unable to update cover photo.'), 'error');
        }
      } catch (e) {
        console.error(e);
        Swal.fire('Error', 'A network error occurred. Please retry.', 'error');
      } finally {
        setUploadingCover(false);
      }
    })();
  }

  function openItem(item) {
    if (item.kind === 'challenge') {
      navigate(`/pages/challenge/${item.id}`);
    } else {
      navigate(`/pages/recipe/${item.linkId ?? item.id}`);
    }
  }

  if (loading && !profile) {
    return <div className="nmpr-loading">Loading profile…</div>;
  }

  const fullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'You';
  const handle = profile?.slug ? `@${profile.slug}` : '';
  const bio = profile?.user_setting?.bio || '';
  const address = formatAddress(profile?.user_setting?.address);
  const selfOwner = { name: fullName, photo: profile?.user_setting?.photo || null };
  const challengeEntries = [
    ...postedChallenges.map((c) => mapPostedChallenge(c, selfOwner)),
    ...participatedChallenges.filter((p) => p?.user_post?.id).map((p) => mapParticipatedChallenge(p, selfOwner)),
  ];
  const madeItItems = madeItList.map((m) => mapMadeIt(m, selfOwner));

  const activeList = activeTab === 'recipes' ? recipes
    : activeTab === 'challenges' ? challengeEntries
      : activeTab === 'saved' ? saved
        : activeTab === 'madeit' ? madeItItems
          : [];

  return (
    <div className="nmpr-root">
      <div className="nmpr-app">
        <main className="nmpr-profile-col">
          <div className="nmpr-banner" style={profile?.user_setting?.cover_photo ? { backgroundImage: `url(${profile.user_setting.cover_photo})` } : undefined}>
            <label className="nmpr-banner-edit" htmlFor="nmprCoverUpload">
              <span>📷</span> {uploadingCover ? 'Uploading…' : 'Edit cover'}
            </label>
            <input id="nmprCoverUpload" type="file" accept="image/*" className="nmpr-file-input" onChange={handleCoverChange} disabled={uploadingCover} />
          </div>

          <div className="nmpr-identity-block">
            <div className="nmpr-id-top-row">
              <Avatar url={profile?.user_setting?.photo} name={fullName} className="nmpr-avatar-xl" />
              <button type="button" className="nmpr-edit-profile-btn" onClick={() => navigate('/dashboard/app/user-profile-edit')}>
                ✎ Edit profile
              </button>
            </div>

            <div className="nmpr-id-name-row">
              <span className="nmpr-id-name">{fullName}</span>
            </div>
            {handle && <p className="nmpr-id-handle">{handle}</p>}
            {bio && <p className="nmpr-id-bio">{bio}</p>}

            {address && (
              <div className="nmpr-id-meta-row">
                <span className="nmpr-id-meta">📍 {address}</span>
              </div>
            )}

            <div className="nmpr-stats-row">
              <div className="nmpr-stat-cell nmpr-clickable" role="button" tabIndex={0} onClick={() => selectTab('recipes')}>
                <div className="nmpr-stat-num">{profile?.post_count ?? 0}</div><div className="nmpr-stat-label">Recipes</div>
              </div>
              <div className="nmpr-stat-cell nmpr-clickable" role="button" tabIndex={0} onClick={() => navigate('/followers')}>
                <div className="nmpr-stat-num">{profile?.follower_count ?? 0}</div><div className="nmpr-stat-label">Followers</div>
              </div>
              <div className="nmpr-stat-cell nmpr-clickable" role="button" tabIndex={0} onClick={() => navigate('/following')}>
                <div className="nmpr-stat-num">{profile?.following_count ?? 0}</div><div className="nmpr-stat-label">Following</div>
              </div>
              <div className="nmpr-stat-cell nmpr-clickable" role="button" tabIndex={0} onClick={() => selectTab('challenges')}>
                <div className="nmpr-stat-num nmpr-accent">{challengeCounts.posted + challengeCounts.participated}</div><div className="nmpr-stat-label">Challenges</div>
              </div>
            </div>
          </div>

          <div className="nmpr-profile-tabs">
            {TABS.map((tab) => (
              <div
                key={tab.key}
                className={`nmpr-ptab${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => selectTab(tab.key)}
                role="button"
                tabIndex={0}
              >
                {tab.label}
                {tab.key === 'recipes' && <span className="nmpr-ptab-count">{recipes.length}</span>}
                {tab.key === 'challenges' && <span className="nmpr-ptab-count">{challengeCounts.posted + challengeCounts.participated}</span>}
                {tab.key === 'saved' && savedLoaded && <span className="nmpr-ptab-count">{saved.length}</span>}
                {tab.key === 'madeit' && <span className="nmpr-ptab-count">{madeItCount}</span>}
              </div>
            ))}
          </div>

          <div className="nmpr-grid-content">
            {activeTab === 'saved' && savedLoading ? (
              <p className="nmpr-empty-note">Loading…</p>
            ) : activeList.length === 0 ? (
              <p className="nmpr-empty-note">
                {activeTab === 'recipes' && (recipesLoaded ? "You haven't posted any recipes yet." : 'Loading…')}
                {activeTab === 'challenges' && "You haven't hosted or joined any challenges yet."}
                {activeTab === 'saved' && "You haven't saved any recipes yet."}
                {activeTab === 'madeit' && "You haven't marked any recipes as made yet."}
              </p>
            ) : (
              <div className="nmpr-recipe-grid">
                {activeList.map((item) => (
                  <RecipeCard key={`${item.kind}-${item.id}`} item={item} onOpen={openItem} />
                ))}
              </div>
            )}
          </div>
        </main>

        <aside className="nmpr-right-rail">
          <div className="nmpr-rail-block">
            <div className="nmpr-rail-head"><h3>People you may know</h3></div>
            {suggestions.length === 0 ? (
              <p className="nmpr-empty-note">No suggestions right now.</p>
            ) : suggestions.map((u) => {
              const name = `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || 'User';
              const isFollowing = u?.is_following === 1;
              return (
                <div key={u.id} className="nmpr-suggestion-card">
                  <Avatar url={u?.user_setting?.photo} name={name} className="nmpr-sc-avatar" />
                  <div className="nmpr-sc-text">
                    <div className="nmpr-sc-name">{name}</div>
                    <div className="nmpr-sc-tag">{u?.follower_count ?? 0} followers</div>
                  </div>
                  <button
                    type="button"
                    className={`nmpr-sc-follow${isFollowing ? ' following' : ''}`}
                    disabled={busyFollowIds.has(u.id)}
                    onClick={() => toggleFollowSuggestion(u.id)}
                  >
                    {isFollowing ? 'Following' : 'Follow'}
                  </button>
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default MyProfilePage;
